import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Divider,
  GlobalStyles,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DirectionsCarFilledRoundedIcon from "@mui/icons-material/DirectionsCarFilledRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import React, { useState } from "react";
import { instance } from "../../Const/ApiHeader";
import { LOGIN } from "../../Const/ApiConst";

const FEATURES = [
  {
    icon: <SpeedRoundedIcon fontSize="small" />,
    title: "Manage vehicles fast",
    text: "Add, duplicate, and update models in seconds.",
  },
  {
    icon: <TuneRoundedIcon fontSize="small" />,
    title: "Full control",
    text: "Manufacturers, subscriptions, and content in one place.",
  },
  {
    icon: <ShieldOutlinedIcon fontSize="small" />,
    title: "Secure access",
    text: "Protected admin sessions for your team.",
  },
];

const glassInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "rgba(255,255,255,0.75)",
    backdropFilter: "blur(6px)",
    transition: "box-shadow .2s ease, background-color .2s ease",
    "& fieldset": { borderColor: "rgba(148,163,184,0.45)" },
    "&:hover fieldset": { borderColor: "rgba(47,107,255,0.55)" },
    "&.Mui-focused": {
      backgroundColor: "#fff",
      boxShadow: "0 0 0 4px rgba(47,107,255,0.15)",
    },
    "&.Mui-focused fieldset": { borderColor: "var(--primary)" },
  },
};

function Login() {
  const [uname, setUname] = useState("");
  const [unameError, setUnameError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState(false);

  const unameEntry = (e) => {
    setUnameError("");
    setLoginError(false);
    setUname(e.target.value);
  };
  const passwordEntry = (e) => {
    setPasswordError("");
    setLoginError(false);
    setPassword(e.target.value);
  };

  const loginAction = async () => {
    if (uname === "") {
      setUnameError("User name cannot be empty");
    } else if (password === "") {
      setPasswordError("Password cannot be empty");
    } else {
      setLoginLoading(true);
      const data = {
        uname: uname,
        password: password,
      };
      try {
        await instance
          .post(LOGIN, data)
          .then((response) => {
            const sessionToken =
              response.data?.token ?? response.data?._id?.toString();
            if (!sessionToken) {
              setLoginLoading(false);
              setLoginError(true);
              return;
            }
            sessionStorage.setItem("wsstfaarvav", sessionToken);
            setLoginLoading(false);
            window.location.replace("/");
          })
          .catch((err) => {
            console.log(err);
            setLoginLoading(false);
            setLoginError(true);
          });
      } catch (error) {
        console.log(error);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !loginLoading) {
      loginAction();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: { xs: 2, md: 3 },
        position: "relative",
        overflow: "hidden",
        background:
          "linear-gradient(120deg, #0b1220 0%, #141d33 40%, #1f2a4d 100%)",
      }}
    >
      <GlobalStyles
        styles={{
          "@keyframes auroraFloat": {
            "0%": { transform: "translate(0, 0) scale(1)" },
            "50%": { transform: "translate(40px, -30px) scale(1.15)" },
            "100%": { transform: "translate(0, 0) scale(1)" },
          },
          "@keyframes cardIn": {
            from: { opacity: 0, transform: "translateY(16px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      />

      {/* Aurora blobs */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          width: 520,
          height: 520,
          top: "-120px",
          left: "-80px",
          borderRadius: "50%",
          filter: "blur(90px)",
          opacity: 0.55,
          background:
            "radial-gradient(circle at 30% 30%, #3b82f6, transparent 70%)",
          animation: "auroraFloat 14s ease-in-out infinite",
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          width: 460,
          height: 460,
          bottom: "-140px",
          right: "-60px",
          borderRadius: "50%",
          filter: "blur(90px)",
          opacity: 0.5,
          background:
            "radial-gradient(circle at 60% 40%, #6366f1, transparent 70%)",
          animation: "auroraFloat 18s ease-in-out infinite reverse",
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          width: 380,
          height: 380,
          top: "40%",
          left: "55%",
          borderRadius: "50%",
          filter: "blur(100px)",
          opacity: 0.35,
          background:
            "radial-gradient(circle at 50% 50%, #22d3ee, transparent 70%)",
          animation: "auroraFloat 22s ease-in-out infinite",
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: 980,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.05fr 1fr" },
          borderRadius: "24px",
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.18)",
          boxShadow: "0 30px 80px rgba(2, 6, 23, 0.55)",
          animation: "cardIn .5s ease both",
          bgcolor: "rgba(255,255,255,0.06)",
          backdropFilter: "blur(18px)",
        }}
      >
        {/* Brand panel */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "space-between",
            p: 5,
            color: "#fff",
            position: "relative",
            background:
              "linear-gradient(160deg, rgba(47,107,255,0.85) 0%, rgba(37,87,215,0.75) 45%, rgba(15,23,42,0.85) 100%)",
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.12,
              backgroundImage:
                "radial-gradient(circle at 20% 20%, #fff 0, transparent 2px)," +
                "radial-gradient(circle at 80% 60%, #fff 0, transparent 2px)",
              backgroundSize: "46px 46px",
            }}
          />
          <Stack direction="row" spacing={1.5} alignItems="center" zIndex={1}>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "14px",
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(255,255,255,0.18)",
                border: "1px solid rgba(255,255,255,0.25)",
              }}
            >
              <DirectionsCarFilledRoundedIcon />
            </Box>
            <Typography variant="h6" fontWeight={700} letterSpacing={0.3}>
              Power Motoring
            </Typography>
          </Stack>

          <Box zIndex={1}>
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{ mb: 1.5, lineHeight: 1.1, letterSpacing: -0.5 }}
            >
              Admin
              <br />
              Control Center
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.85, mb: 4, maxWidth: 360 }}>
              Everything you need to run the catalog, from one clean, modern
              dashboard.
            </Typography>

            <Stack spacing={2.25}>
              {FEATURES.map((f) => (
                <Stack
                  key={f.title}
                  direction="row"
                  spacing={2}
                  alignItems="flex-start"
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      flexShrink: 0,
                      borderRadius: "12px",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "rgba(255,255,255,0.16)",
                      border: "1px solid rgba(255,255,255,0.2)",
                    }}
                  >
                    {f.icon}
                  </Box>
                  <Box>
                    <Typography fontWeight={600}>{f.title}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {f.text}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Typography variant="caption" sx={{ opacity: 0.7 }} zIndex={1}>
            © {new Date().getFullYear()} Power Motoring. All rights reserved.
          </Typography>
        </Box>

        {/* Form panel */}
        <Box
          sx={{
            p: { xs: 3.5, sm: 5 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            bgcolor: "rgba(255,255,255,0.92)",
            backdropFilter: "blur(12px)",
          }}
        >
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            sx={{ display: { xs: "flex", md: "none" }, mb: 2 }}
          >
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                background:
                  "linear-gradient(135deg, #2f6bff, #2457d7)",
                color: "#fff",
              }}
            >
              <DirectionsCarFilledRoundedIcon fontSize="small" />
            </Box>
            <Typography variant="h6" fontWeight={700}>
              Power Motoring
            </Typography>
          </Stack>

          <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: -0.5 }}>
            Welcome back
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, mb: 3.5 }}>
            Sign in to your admin account to continue.
          </Typography>

          {loginError ? (
            <Alert severity="error" variant="filled" sx={{ mb: 2, borderRadius: "12px" }}>
              Invalid credentials. Please try again.
            </Alert>
          ) : null}

          <TextField
            fullWidth
            sx={{ mb: 2.25, ...glassInputSx }}
            value={uname}
            error={Boolean(unameError)}
            helperText={unameError}
            label="User ID"
            onKeyDown={handleKeyDown}
            onChange={(e) => unameEntry(e)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineRoundedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            sx={{ mb: 3.5, ...glassInputSx }}
            value={password}
            error={Boolean(passwordError)}
            helperText={passwordError}
            type={showPassword ? "text" : "password"}
            label="Password"
            onKeyDown={handleKeyDown}
            onChange={(e) => passwordEntry(e)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((s) => !s)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? (
                      <VisibilityOff fontSize="small" />
                    ) : (
                      <Visibility fontSize="small" />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <LoadingButton
            fullWidth
            loading={loginLoading}
            variant="contained"
            size="large"
            onClick={() => loginAction()}
            sx={{
              py: 1.35,
              borderRadius: "12px",
              fontSize: "1rem",
              background: "linear-gradient(135deg, #2f6bff, #2457d7)",
              boxShadow: "0 10px 24px rgba(47,107,255,0.35)",
              "&:hover": {
                background: "linear-gradient(135deg, #2860f0, #1f4dc4)",
                boxShadow: "0 12px 28px rgba(47,107,255,0.45)",
              },
            }}
          >
            Sign in
          </LoadingButton>

          <Divider sx={{ my: 3 }} />
          <Typography variant="caption" color="text.secondary" align="center">
            Authorized personnel only. Contact your administrator for access.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default Login;
